/**
 * Férias (P1) — lista dos RECIBOS de férias (processamentos): cada recibo é um
 * documento com configuração persistente (pessoa, período de gozo, competência
 * de pagamento) e ciclo próprio. "Novo recibo" abre o formulário guiado com a
 * validação de fracionamento inline (o conjunto do ano é checado na API — CLT
 * art. 134: até 3 períodos, um ≥ 14 dias, cada ≥ 5, soma ≤ 30).
 */
import { useEffect, useState } from "react";
import { Badge, Button, Card, Group, Modal, NumberInput, SegmentedControl, Select, Switch, Text, Textarea, ThemeIcon, Title, Alert } from "@mantine/core";
import { IconBeach, IconPlus, IconAlertTriangle, IconArrowLeft } from "@tabler/icons-react";
import { api } from "../api";
import { navegar, useQueryRota, origemCompetencia, rotuloVoltarCompetencia } from "../roteador";
import { fmtCompetencia, fmtData, fmtMoeda } from "../format";
import { ItemEntidade, TabelaDados, Th, TdNum } from "../componentes/listas";
import { BadgeEstadoProcessamento } from "../componentes/relatorio";
import { CompetenciaField, DataField, MoedaField } from "../componentes/entradas-mantine";
import { ErroApiAlert, notificarOk, notificarAviso, notificarErroApi } from "../componentes/notificacoes";

export interface ProcessamentoResumo {
  id: string;
  tipo: string;
  estado: string;
  funcionarioId: string | null;
  competenciaPagamento: number;
  ano: number | null;
  dataInicio: string | null;
  dias: number | null;
  observacao: string | null;
  funcionario: { id: string; nome: string; matriculaDominio: number | null } | null;
  tipoRotulo: string;
  resumoRun: { liquidoCentavos: number; executadoEm: string; motorVersion: string } | null;
}

interface FuncionarioOpcao {
  id: string;
  nome: string;
  matriculaDominio: number | null;
}

/** M1.1 — solicitação elegível do seletor (GET /ferias/solicitacoes-elegiveis). */
export interface SolicitacaoElegivel {
  id: string;
  funcionarioId: string;
  nomeFuncionario: string;
  funcionarioAtivo: boolean;
  status: string;
  inicioGozo: string;
  fimGozo: string | null;
  diasGozo: number;
  diasAbono: number;
  antecipacao13: boolean;
  enviadoContabilidade: boolean;
  consumoEfetivoDias: number;
  competenciaPagamento: number;
  periodoAquisitivo: {
    id: string;
    inicioAquisitivo: string;
    fimAquisitivo: string;
    fimConcessivo: string | null;
    diasDireito: number;
    saldoDerivado: number;
  } | null;
  coletiva: { id: string; titulo: string | null; diasDebitados: number | null; diasRecesso: number | null } | null;
  alertas: string[];
}

/** E10d — visão geral do estoque de férias (GET /ferias/alertas). */
interface AlertaFeriasVencimento {
  periodoId: string;
  funcionarioId: string;
  nomeFuncionario: string;
  tipo: "vencida" | "a_vencer";
  fimConcessivo: string;
  saldoDerivado: number;
  diasParaLimite: number;
}
interface AlertasFerias {
  vencidas: AlertaFeriasVencimento[];
  aVencer: AlertaFeriasVencimento[];
  /** 26/08/2026 (pedido do dono) — recibos NÃO fechados com o gozo em curso ou
   *  começando em ≤ 2 dias (art. 145: o pagamento é até 2 dias antes do gozo). */
  prazoRecibo?: {
    processamentoId: string;
    nomeFuncionario: string;
    inicioGozo: string;
    fimGozo: string;
    estado: string;
    diasParaOGozo: number;
    mensagem: string;
  }[];
  divergencias: string[];
}

export function dataFim(inicioIso: string, dias: number): string {
  const d = new Date(`${inicioIso}T00:00:00.000Z`);
  return new Date(d.getTime() + (dias - 1) * 24 * 3600 * 1000).toISOString().slice(0, 10);
}

export function FeriasPage() {
  const [lista, setLista] = useState<ProcessamentoResumo[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [carregando, setCarregando] = useState(true);
  const [novoAberto, setNovoAberto] = useState(false);
  // M1.8 — deep-link de recibo pré-preenchido: "#/ferias?novo=1&func=...&
  // comp=...&inicio=...&dias=..." abre o formulário manual já preenchido
  // (sugestão da reconciliação M1.6, pelas datas do ponto — D10: o sistema não
  // decide a fonte prevalente). Com "&sol=..." (alerta M1.2 da competência —
  // férias aprovada no Convenia sem recibo), além de preencher o manual, tenta
  // PRÉ-SELECIONAR a solicitação no seletor: criar pelo seletor grava o
  // vínculo explícito e o alerta resolve no ato; se ela não estiver mais
  // elegível, fica a digitação manual preenchida (campos editáveis).
  const queryRota = useQueryRota();
  // 05/09/2026 (pedido do dono) — origem do deep-link (ex.: /competencias/202608):
  // com ela, a página mostra "Voltar para a competência MM/AAAA" e a navegação
  // para o detalhe do recibo criado preserva a origem (mesmo botão lá)
  const origem = origemCompetencia(queryRota);
  // E10d — visão geral do estoque (vencimentos concessivos × saldo derivado)
  const [alertasEstoque, setAlertasEstoque] = useState<AlertasFerias | null>(null);
  // formulário do novo recibo
  const [funcionarios, setFuncionarios] = useState<FuncionarioOpcao[]>([]);
  const [fFunc, setFFunc] = useState<string | null>(null);
  const [fComp, setFComp] = useState<number | null>(null);
  const [fInicio, setFInicio] = useState<string | null>(null);
  const [fDias, setFDias] = useState<number | "">("");
  const [fAbono, setFAbono] = useState<number | "">(""); // M1.3 — abono pecuniário no fluxo manual
  const [fAnt13, setFAnt13] = useState(false); // M1.4 — adiantamento de 13º no fluxo manual
  const [fMedia, setFMedia] = useState<number | null>(null);
  const [fObs, setFObs] = useState("");
  const [erroNovo, setErroNovo] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);
  // M1.1 — seletor solicitação→recibo: elegíveis da pessoa escolhida
  const [elegiveis, setElegiveis] = useState<SolicitacaoElegivel[] | null>(null);
  const [modo, setModo] = useState<"solicitacao" | "manual">("manual");
  const [fSol, setFSol] = useState<string | null>(null);
  // Deep-link com "&sol=" (alerta M1.2): solicitação a pré-selecionar no
  // seletor assim que as elegíveis da pessoa chegarem (null = nada pendente)
  const [solPendente, setSolPendente] = useState<string | null>(null);

  async function carregar() {
    const r = await api.get<{ processamentos: ProcessamentoResumo[] }>("/processamentos?tipo=ferias");
    setLista(r.processamentos);
  }

  useEffect(() => {
    setCarregando(true);
    carregar()
      .catch(setErro)
      .finally(() => setCarregando(false));
    api
      .get<{ funcionarios: FuncionarioOpcao[] }>("/funcionarios?tipoVinculo=clt&ativo=true")
      .then((r) => setFuncionarios(r.funcionarios.filter((f) => f.matriculaDominio !== null)))
      .catch(() => undefined);
    api
      .get<AlertasFerias>("/ferias/alertas")
      .then((r) => {
        // tolerante a payload inesperado (estoque não sincronizado / rota antiga)
        if (Array.isArray(r?.vencidas) && Array.isArray(r?.aVencer)) setAlertasEstoque(r);
      })
      .catch(() => undefined); // estoque ainda não sincronizado — cartão simplesmente não aparece
  }, []);

  // M1.8 — deep-link "novo recibo pré-preenchido" (reconciliação M1.6, pelas
  // datas do ponto; alerta M1.2 da competência, com "&sol="). Roda quando a
  // query do hash muda.
  useEffect(() => {
    if (queryRota.get("novo") !== "1") return;
    const func = queryRota.get("func");
    const comp = Number(queryRota.get("comp"));
    const inicio = queryRota.get("inicio");
    const dias = Number(queryRota.get("dias"));
    const sol = queryRota.get("sol");
    setModo("manual");
    setFSol(null);
    setElegiveis(null);
    setSolPendente(null);
    if (func && sol) {
      // alerta M1.2: carrega as elegíveis da pessoa para pré-selecionar a
      // solicitação no seletor (efeito abaixo); o manual fica preenchido como
      // fallback editável se ela não estiver mais elegível
      trocarFuncionario(func);
      setSolPendente(sol);
    } else if (func) {
      setFFunc(func);
    }
    if (Number.isInteger(comp) && comp > 0) setFComp(comp);
    if (inicio) setFInicio(inicio);
    if (Number.isInteger(dias) && dias > 0) setFDias(dias);
    setNovoAberto(true);
    // limpa a query para não reabrir o modal na próxima navegação interna
    // (a ORIGEM é preservada — o botão "Voltar para a competência" depende dela)
    const o = queryRota.get("origem");
    window.history.replaceState(null, "", o ? `#/ferias?origem=${encodeURIComponent(o)}` : "#/ferias");
  }, [queryRota]);

  // Deep-link com "&sol=" (alerta M1.2): quando as elegíveis da pessoa chegam,
  // pré-seleciona a solicitação do alerta no seletor (criar por ele grava o
  // vínculo explícito → o alerta da competência resolve no ato). Não elegível
  // mais (já vinculada/desligada) → fica a digitação manual preenchida.
  useEffect(() => {
    if (!solPendente || elegiveis === null) return;
    const alvo = elegiveis.find((s) => s.id === solPendente);
    if (alvo && alvo.funcionarioAtivo) {
      setFSol(alvo.id);
      setModo("solicitacao");
    }
    setSolPendente(null);
  }, [elegiveis, solPendente]);

  /** M1.1 — ao escolher a pessoa (passo 1), busca as solicitações aprovadas
   *  elegíveis (passo 2). Se houver, o modo "solicitação" vira o padrão; a
   *  digitação manual segue disponível como fallback (diretriz 12/13). */
  function trocarFuncionario(id: string | null) {
    setFFunc(id);
    setFSol(null);
    setElegiveis(null);
    if (!id) {
      setModo("manual");
      return;
    }
    api
      .get<{ total: number; solicitacoes: SolicitacaoElegivel[] }>(`/ferias/solicitacoes-elegiveis?funcionarioId=${id}`)
      .then((r) => {
        setElegiveis(r.solicitacoes);
        setModo(r.solicitacoes.length > 0 ? "solicitacao" : "manual");
      })
      .catch((e) => {
        setElegiveis([]);
        setModo("manual"); // estoque não sincronizado — o manual sempre funciona
        // 30/07/2026: a falha ficava SILENCIOSA (parecia "o seletor não lista
        // mais") — agora avisa o motivo e orienta a sincronização
        notificarAviso(
          `Não consegui buscar as férias aprovadas no Convenia (${e instanceof Error ? e.message : String(e)}) — ` +
            "a digitação manual segue disponível. Rode o sync do Convenia (domínio férias) e tente de novo.",
        );
      });
  }

  async function criar() {
    setSalvando(true);
    setErroNovo(null);
    try {
      const payload =
        modo === "solicitacao" && fSol
          ? {
              tipo: "ferias",
              solicitacaoFeriasId: fSol,
              mediasOverrideCentavos: fMedia,
              observacao: fObs.trim() || null,
            }
          : {
              tipo: "ferias",
              funcionarioId: fFunc,
              competenciaPagamento: fComp,
              dataInicio: fInicio,
              dias: fDias === "" ? null : fDias,
              diasAbono: fAbono === "" || fAbono === 0 ? null : fAbono,
              antecipacao13: fAnt13 ? true : null,
              mediasOverrideCentavos: fMedia,
              observacao: fObs.trim() || null,
            };
      const criado = await api.post<{ id: string; alertas?: string[] }>("/processamentos", payload);
      notificarOk("Recibo de férias criado — revise e calcule.");
      if (criado.alertas && criado.alertas.length > 0) notificarAviso(criado.alertas[0]!);
      setNovoAberto(false);
      navegar(`/ferias/${criado.id}${origem ? `?origem=${encodeURIComponent(origem)}` : ""}`);
    } catch (e) {
      setErroNovo(e);
      notificarErroApi(e);
    } finally {
      setSalvando(false);
    }
  }

  const pronto =
    modo === "solicitacao"
      ? fSol !== null
      : fFunc !== null && fComp !== null && fInicio !== null && fDias !== "" && fDias >= 1;
  const solSelecionada = modo === "solicitacao" && fSol ? (elegiveis?.find((s) => s.id === fSol) ?? null) : null;

  return (
    <>
      {origem && (
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={16} />}
          mb="xs"
          data-testid="voltar-competencia"
          onClick={() => navegar(origem)}
        >
          {rotuloVoltarCompetencia(origem)}
        </Button>
      )}
      <Group justify="space-between" mb="xs" wrap="wrap">
        <div>
          <Title order={2}>Férias</Title>
          <Text size="sm" c="dimmed">
            Recibos de férias: um documento por pessoa e período, com configuração própria — calcule, imprima o recibo e
            feche. A folha do mês desconta os dias automaticamente (sem lançamento manual).
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setNovoAberto(true)}>
          Novo recibo
        </Button>
      </Group>
      <ErroApiAlert erro={erro} />

      {alertasEstoque && (alertasEstoque.vencidas.length > 0 || alertasEstoque.aVencer.length > 0) && (
        <Alert color={alertasEstoque.vencidas.length > 0 ? "red" : "yellow"} icon={<IconAlertTriangle size={16} />} mb="md">
          <Text size="sm" fw={600} mb={4}>
            Estoque de férias (sync Convenia): {alertasEstoque.vencidas.length} período(s) vencido(s) ·{" "}
            {alertasEstoque.aVencer.length} a vencer em 60 dias
          </Text>
          {[...alertasEstoque.vencidas, ...alertasEstoque.aVencer].map((a) => (
            <Text key={a.periodoId} size="sm">
              • {a.nomeFuncionario}: {a.saldoDerivado} dia(s) — limite concessivo {fmtData(a.fimConcessivo)}
              {a.tipo === "vencida" ? " (VENCIDO — pagamento em dobro, CLT art. 137)" : ` (${a.diasParaLimite} dias)`}
            </Text>
          ))}
          <Text size="xs" c="dimmed" mt={4}>
            Saldos derivados (direito − consumos). Detalhe por pessoa: ficha do funcionário, aba “Férias &amp; afastamentos”.
          </Text>
        </Alert>
      )}

      {/* 26/08/2026 (pedido do dono) — recibo NÃO fechado com o gozo em curso ou
          começando em ≤ 2 dias (art. 145: o pagamento é até 2 dias antes do gozo).
          Orientativo — nunca bloqueia. */}
      {(alertasEstoque?.prazoRecibo ?? []).length > 0 && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} mb="md">
          <Text size="sm" fw={600} mb={4}>
            Prazo de pagamento de férias (o recibo tem que estar PAGO até 2 dias antes do gozo — art. 145)
          </Text>
          {alertasEstoque!.prazoRecibo!.map((a) => (
            <Text key={a.processamentoId} size="sm">
              • {a.mensagem}
            </Text>
          ))}
        </Alert>
      )}

      {carregando ? (
        <Text c="dimmed" size="sm">Carregando…</Text>
      ) : lista.length === 0 ? (
        <Text c="dimmed" size="sm">Nenhum recibo ainda — crie o primeiro em “Novo recibo”.</Text>
      ) : (
        lista.map((p) => (
          <ItemEntidade
            key={p.id}
            icone={
              <ThemeIcon variant="light" color="teal" size="lg">
                <IconBeach size={18} />
              </ThemeIcon>
            }
            titulo={p.funcionario?.nome ?? "—"}
            linhaSecundaria={
              p.dataInicio && p.dias
                ? `${fmtData(p.dataInicio)} a ${fmtData(dataFim(p.dataInicio, p.dias))} · ${p.dias} dias · pagamento em ${fmtCompetencia(p.competenciaPagamento)}`
                : `pagamento em ${fmtCompetencia(p.competenciaPagamento)} · período a definir`
            }
            badges={
              <>
                <BadgeEstadoProcessamento estado={p.estado} />
                {p.resumoRun && (
                  <Badge variant="light" color="sillis">
                    líquido {fmtMoeda(p.resumoRun.liquidoCentavos)}
                  </Badge>
                )}
              </>
            }
            onClick={() => navegar(`/ferias/${p.id}`)}
            ariaLabel={`Abrir recibo de férias de ${p.funcionario?.nome ?? ""}`}
          />
        ))
      )}

      <Modal opened={novoAberto} onClose={() => setNovoAberto(false)} title="Novo recibo de férias" centered size="lg">
        <Text size="sm" c="dimmed" mb="sm">
          Um recibo = uma pessoa × um período de gozo × um mês de pagamento. Escolha a pessoa e o período APROVADO no
          Convenia (sem digitar datas) — ou digite manualmente. Para fracionar, crie um recibo por período — a
          validação olha o conjunto do ano (até 3 períodos, um ≥ 14 dias, cada ≥ 5, soma ≤ 30).
        </Text>
        <ErroApiAlert erro={erroNovo} />
        <Select
          label="1. Pessoa"
          placeholder="Selecione"
          data={funcionarios.map((f) => ({ value: f.id, label: f.nome }))}
          value={fFunc}
          onChange={trocarFuncionario}
          searchable
          mb="sm"
        />
        {fFunc !== null && elegiveis !== null && elegiveis.length > 0 && (
          <>
            <Text size="sm" fw={600} mb={4}>
              2. Período de férias
            </Text>
            <SegmentedControl
              fullWidth
              mb="sm"
              value={modo}
              onChange={(v) => setModo(v as "solicitacao" | "manual")}
              data={[
                { value: "solicitacao", label: `Aprovado no Convenia (${elegiveis.length})` },
                { value: "manual", label: "Digitação manual" },
              ]}
            />
          </>
        )}
        {fFunc !== null && modo === "solicitacao" && elegiveis !== null && elegiveis.length > 0 && (
          <>
            {elegiveis.map((s) => (
              <Card
                key={s.id}
                withBorder
                shadow="xs"
                mb="xs"
                padding="sm"
                onClick={() => s.funcionarioAtivo && setFSol(s.id)}
                style={{
                  cursor: s.funcionarioAtivo ? "pointer" : "not-allowed",
                  borderColor: fSol === s.id ? "var(--mantine-color-sillis-6)" : undefined,
                  borderWidth: fSol === s.id ? 2 : undefined,
                  opacity: s.funcionarioAtivo ? 1 : 0.6,
                }}
              >
                <Group justify="space-between" wrap="wrap">
                  <Text size="sm" fw={600}>
                    {fmtData(s.inicioGozo)} a {fmtData(s.fimGozo ?? dataFim(s.inicioGozo, s.diasGozo))} · {s.diasGozo} dias
                  </Text>
                  <Group gap={4}>
                    {s.diasAbono > 0 && (
                      <Badge variant="light" color="grape">
                        +{s.diasAbono}d abono (calculado)
                      </Badge>
                    )}
                    {s.antecipacao13 && (
                      <Badge variant="light" color="indigo">
                        13º antecipado (calculado)
                      </Badge>
                    )}
                    {s.coletiva && (
                      <Badge variant="light" color="orange">
                        Coletiva{s.coletiva.titulo ? `: ${s.coletiva.titulo}` : ""}
                        {s.coletiva.diasDebitados !== null
                          ? ` (${s.coletiva.diasDebitados}d férias${(s.coletiva.diasRecesso ?? 0) > 0 ? ` + ${s.coletiva.diasRecesso}d recesso` : ""})`
                          : ""}
                      </Badge>
                    )}
                    {s.status === "accounting" && (
                      <Badge variant="light" color="gray">
                        enviada à contabilidade
                      </Badge>
                    )}
                    {!s.funcionarioAtivo && <Badge color="red">pessoa inativa</Badge>}
                  </Group>
                </Group>
                <Text size="xs" c="dimmed" mt={4}>
                  {s.periodoAquisitivo
                    ? `Período aquisitivo ${fmtData(s.periodoAquisitivo.inicioAquisitivo)}–${fmtData(s.periodoAquisitivo.fimAquisitivo)} · saldo derivado ${s.periodoAquisitivo.saldoDerivado} dia(s)`
                    : "Sem vínculo de período aquisitivo — o saldo derivado não a consome"}
                  {" · pagamento em "}
                  {fmtCompetencia(s.competenciaPagamento)}
                </Text>
                {s.alertas.map((a, i) => (
                  <Text key={i} size="xs" c="orange" mt={2}>
                    ⚠ {a}
                  </Text>
                ))}
              </Card>
            ))}
            {solSelecionada && (
              <Text size="sm" c="dimmed" mb="sm">
                Selecionado: gozo {fmtData(solSelecionada.inicioGozo)} ({solSelecionada.diasGozo} dias
                {solSelecionada.diasAbono > 0 ? ` + ${solSelecionada.diasAbono}d de abono` : ""}
                {solSelecionada.antecipacao13 ? ", com adiantamento de 13º" : ""}) · pagamento em{" "}
                {fmtCompetencia(solSelecionada.competenciaPagamento)} — tudo derivado da solicitação, sem digitação.
              </Text>
            )}
          </>
        )}
        {modo === "manual" && (
          <>
            <div style={{ marginBottom: "var(--mantine-spacing-sm)" }}>
              <CompetenciaField label="Competência de pagamento" valor={fComp} onChange={setFComp} />
            </div>
            <Group grow mb="sm">
              <DataField label="Início do gozo" valor={fInicio} onChange={setFInicio} />
              <NumberInput label="Dias corridos" min={1} max={30} value={fDias} onChange={(v) => setFDias(v === "" ? "" : Number(v))} />
              <NumberInput
                label="Abono pecuniário (dias, opcional)"
                description="Venda de até 1/3 das férias (art. 143) — calculado sem INSS/IRRF/FGTS"
                min={0}
                max={10}
                value={fAbono}
                onChange={(v) => setFAbono(v === "" ? "" : Number(v))}
              />
            </Group>
            <Switch
              mb="sm"
              label="Adiantar a 1ª parcela do 13º junto com as férias"
              description="50% da projeção, sem INSS/IRRF, com FGTS 8% — descontado na 2ª parcela de dezembro (requerimento legal: janeiro)"
              checked={fAnt13}
              onChange={(e) => setFAnt13(e.currentTarget.checked)}
            />
          </>
        )}
        <Group grow mb="sm">
          <MoedaField
            label="Média de variáveis (opcional)"
            description="Vazio = automática (histórico de 12 meses ou zerada com aviso)"
            valor={fMedia}
            onChange={setFMedia}
          />
        </Group>
        <Textarea label="Observação (opcional)" value={fObs} onChange={(e) => setFObs(e.currentTarget.value)} mb="md" minRows={2} />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setNovoAberto(false)}>
            Voltar
          </Button>
          <Button onClick={() => void criar()} disabled={!pronto} loading={salvando}>
            Criar recibo
          </Button>
        </Group>
      </Modal>
    </>
  );
}

/** Tabela de eventos do resultado (holerite do recibo / do 13º) — usada pelas
 *  páginas de detalhe dos processamentos. */
export interface EventoResultado {
  codigoDominio: number;
  descricao: string;
  tipo: "P" | "D";
  referencia: { tipo: string; valorCentavos?: number; porcentagem?: number; minutos?: number };
  valorCentavos: number;
}

export function TabelaEventosResultado({ eventos, tipo }: { eventos: EventoResultado[]; tipo: "P" | "D" }) {
  const lista = eventos.filter((e) => e.tipo === tipo);
  return (
    <TabelaDados>
      <thead>
        <tr>
          <Th num>Cód.</Th>
          <Th>{tipo === "P" ? "Provento" : "Desconto"}</Th>
          <Th num>Referência</Th>
          <Th num>Valor (R$)</Th>
        </tr>
      </thead>
      <tbody>
        {lista.map((e, i) => (
          <tr key={i}>
            <td style={{ textAlign: "right", color: "var(--mantine-color-gray-6)" }}>{e.codigoDominio}</td>
            <td>{e.descricao}</td>
            <TdNum>
              {e.referencia.tipo === "percentual" && e.referencia.porcentagem !== undefined
                ? `${e.referencia.porcentagem.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
                : fmtMoeda(e.valorCentavos)}
            </TdNum>
            <TdNum>{fmtMoeda(e.valorCentavos)}</TdNum>
          </tr>
        ))}
        {lista.length === 0 && (
          <tr>
            <td colSpan={4}>
              <Text size="sm" c="dimmed">—</Text>
            </td>
          </tr>
        )}
      </tbody>
    </TabelaDados>
  );
}
